/**
 * t335u item 7: the manager dashboard. One page per building (or All buildings): who's in now, open issues by
 * priority and SLA, overdue checklists, labour hours against the rota, expiring skills and training, and equipment
 * due. Each tile only appears where the viewer holds that tile's permission at the building, and links through.
 * Shared by the API, desktop, phone web and the native app.
 */
import { ON_SITE_PERMS } from './visitors';

export type DashTile = 'on_site' | 'issues' | 'checklists' | 'labour' | 'people' | 'equipment';

/** Per tile: holding any of these at a building shows the tile for that building. */
export const DASH_TILE_PERMS: Record<DashTile, readonly string[]> = {
  on_site: ON_SITE_PERMS,
  issues: ['ops.incident.view_all', 'ops.incident.manage'],
  checklists: ['ops.checklist.view_all'],
  labour: ['workforce.reports.view', 'workforce.timesheet.approve', 'workforce.rota.edit'],
  people: ['workforce.skills.manage', 'workforce.onboarding.manage'],
  equipment: ['ops.incident.manage', 'ops.incident.settings', 'ops.checklist.template.manage'],
};
/** Anyone holding one of these anywhere gets the dashboard (route guard, nav and More menu). */
export const DASHBOARD_PERMS: readonly string[] = [...new Set(Object.values(DASH_TILE_PERMS).flat())];
/** Skills expiring within this many days (or the workspace's longest reminder) count as "soon". */
export const DASH_SKILL_SOON_DAYS = 30;
export const DASH_TRAINING_SOON_DAYS = 7;
export const DASH_EQUIPMENT_SOON_DAYS = 30;
/** Rows listed under a tile; the tile's link shows the rest. */
export const DASH_LIST_MAX = 5;

export interface DashBuilding { id: string; name: string; timezone: string }

export interface DashOnSite { staff: number; on_break: number; visitors: number; people: { name: string; detail: string | null; kind: 'staff' | 'visitor'; since: string }[] }
export interface DashIssue { id: string; ref: string | null; title: string; severity: 'low' | 'medium' | 'high' | 'critical'; location_name: string; resolve_due_at: string | null; created_at: string }
export interface DashIssues { open: number; critical: number; high: number; medium: number; low: number; breached: number; response_overdue: number; items: DashIssue[] }
export interface DashRun { id: string; name: string; location_name: string; due_by: string | null; status: 'open' | 'in_progress' | 'missed' }
export interface DashChecklists { overdue: number; to_do: number; done: number; items: DashRun[] }
export interface DashHours { planned_min: number; planned_so_far_min: number; clocked_min: number }
export interface DashLabour { today: DashHours; week: DashHours; week_from: string; week_to: string }
export interface DashPersonDue { user_id: string; name: string; what: string; date: string; link: string }
export interface DashPeople {
  skills: { expired: number; soon: number } | null;
  training: { overdue: number; soon: number } | null;
  items: DashPersonDue[];
}
export interface DashEquipmentItem { id: string; name: string; location_name: string; what: string; date: string }
export interface DashEquipment { overdue: number; soon: number; items: DashEquipmentItem[] }

export interface Dashboard {
  /** A building id, or 'all'. */
  scope: string;
  /** Buildings this view covers (one, or every building where I see at least one tile). */
  buildings: DashBuilding[];
  /** Building dates are local; for All buildings, each building's own today. */
  today: string;
  generated_at: string;
  on_site: DashOnSite | null;
  issues: DashIssues | null;
  checklists: DashChecklists | null;
  labour: DashLabour | null;
  people: DashPeople | null;
  equipment: DashEquipment | null;
  /** For All buildings: the same counts per building (tiles the viewer has there), for the breakdown. */
  by_building: DashBuildingRow[];
}
export interface DashBuildingRow {
  id: string; name: string;
  on_site: number | null; issues_open: number | null; issues_breached: number | null; checklists_overdue: number | null;
  clocked_today_min: number | null; planned_today_min: number | null; people_due: number | null; equipment_due: number | null;
}
export interface DashboardBuildings { buildings: DashBuilding[]; can_all: boolean }

/**
 * t553u: where a building picked in the dashboard filter is remembered on this device. Only a pick made in the filter
 * is written, never the starting default, so a new default can't be pinned by an old automatic value.
 */
export const dashboardPickKey = (ws: string) => `hub.dashboard.building:${ws}`;
/**
 * t553u: which building the manager dashboard shows: ?building= (or the remembered pick) when I can still see it, else
 * All buildings with two or more (as Visitors, t534u), else my only building. Before t553u it fell back to the first.
 */
export function dashboardBuildingChoice(b: { buildings: readonly { id: string }[]; can_all: boolean } | null | undefined, picked: string | null | undefined): string {
  if (!b) return '';
  if (picked === 'all' && b.can_all) return 'all';
  const own = b.buildings.find(x => x.id === picked);
  if (own) return own.id;
  return b.can_all ? 'all' : b.buildings[0]?.id ?? '';
}

/** "7h 30m", "45m", "0h". */
export function dashHours(min: number): string {
  const m = Math.max(0, Math.round(min));
  const h = Math.floor(m / 60), r = m % 60;
  if (!h) return r ? `${r}m` : '0h';
  return r ? `${h}h ${r}m` : `${h}h`;
}

/** Clocked against what the rota planned so far: "2h 15m over", "1h under", "On plan" (within 15 minutes). */
export function dashVariance(h: DashHours): { text: string; tone: 'ok' | 'over' | 'under' } {
  const d = Math.round(h.clocked_min - h.planned_so_far_min);
  if (Math.abs(d) < 15) return { text: 'On plan so far', tone: 'ok' };
  return d > 0 ? { text: `${dashHours(d)} over plan so far`, tone: 'over' } : { text: `${dashHours(-d)} under plan so far`, tone: 'under' };
}

/** 0–100 for a quiet bar: clocked as a share of the whole day's (or week's) plan. */
export function dashShare(h: DashHours): number {
  if (h.planned_min <= 0) return h.clocked_min > 0 ? 100 : 0;
  return Math.max(0, Math.min(100, Math.round((h.clocked_min / h.planned_min) * 100)));
}

/** "Overdue by 3 days", "Due today", "Due in 5 days", "Expired 2 days ago", "Expires in 12 days". */
export function dashDue(date: string, today: string, kind: 'due' | 'expires' = 'due'): { text: string; late: boolean } {
  const days = Math.round((Date.parse(`${date}T00:00:00Z`) - Date.parse(`${today}T00:00:00Z`)) / 86_400_000);
  const n = (x: number) => `${x} ${x === 1 ? 'day' : 'days'}`;
  if (kind === 'expires') {
    if (days < 0) return { text: `Expired ${n(-days)} ago`, late: true };
    if (days === 0) return { text: 'Expires today', late: false };
    return { text: `Expires in ${n(days)}`, late: false };
  }
  if (days < 0) return { text: `Overdue by ${n(-days)}`, late: true };
  if (days === 0) return { text: 'Due today', late: false };
  return { text: `Due in ${n(days)}`, late: false };
}

/** Where each tile links (desktop paths; the app maps them). On a phone, "who's late" opens Today's Who's in. */
export function dashLinks(scope: string, opts: { phone?: boolean } = {}) {
  const one = scope !== 'all' ? scope : null;
  const q = (k: string) => (one ? `${k}=${one}` : '');
  const join = (path: string, ...parts: string[]) => { const s = parts.filter(Boolean).join('&'); return s ? `${path}?${s}` : path; };
  return {
    on_site: join('/visitors', q('building')),
    issues: join('/incidents', 'scope=all', q('location_id')),
    issues_breached: join('/incidents', 'scope=all', 'breached=1', q('location_id')),
    issues_critical: join('/incidents', 'scope=all', 'severity=critical', q('location_id')),
    checklists: '/checklists',
    labour: join('/workforce/reports/variance', q('location_id')),
    attendance: opts.phone ? '/today' : '/workforce/timesheets?tab=attendance',
    skills: '/workforce/skills',
    training: '/training?tab=people',
    equipment: '/equipment',
  };
}

const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
/** The hours tile's week label, short enough for one line: "This week · 19–25 Oct", "This week · 27 Oct – 2 Nov". */
export function dashWeekLabel(from: string, to: string): string {
  const [, fm, fd] = from.split('-').map(Number) as [number, number, number];
  const [, tm, td] = to.split('-').map(Number) as [number, number, number];
  return fm === tm ? `This week · ${fd}–${td} ${MON[tm - 1]}` : `This week · ${fd} ${MON[fm - 1]} – ${td} ${MON[tm - 1]}`;
}

/** One building's counts as a line for the phone's By building list: "3 in now · 6 open issues (6 past SLA) · 46h of 115h today". */
export function dashBuildingLine(b: DashBuildingRow): string {
  const n = (x: number, one: string, many: string) => `${x} ${x === 1 ? one : many}`;
  const hours = b.clocked_today_min == null ? null : !b.planned_today_min && !b.clocked_today_min ? 'No shifts today' : `${dashHours(b.clocked_today_min)} of ${dashHours(b.planned_today_min ?? 0)} today`;
  return [
    b.on_site != null ? `${b.on_site} in now` : null,
    b.issues_open != null ? `${n(b.issues_open, 'open issue', 'open issues')}${b.issues_breached ? ` (${b.issues_breached} past SLA)` : ''}` : null,
    b.checklists_overdue ? n(b.checklists_overdue, 'checklist overdue', 'checklists overdue') : null,
    hours,
    b.people_due ? n(b.people_due, 'licence or course due', 'licences or courses due') : null,
    b.equipment_due ? `${b.equipment_due} equipment due` : null,
  ].filter(Boolean).join(' · ');
}

/** Which tiles have something to look at (the rest are calm "nothing due" lines). */
export function dashNeedsLook(d: Dashboard): DashTile[] {
  const out: DashTile[] = [];
  if (d.issues && (d.issues.breached || d.issues.critical || d.issues.response_overdue)) out.push('issues');
  if (d.checklists?.overdue) out.push('checklists');
  if (d.people && ((d.people.skills?.expired ?? 0) + (d.people.training?.overdue ?? 0))) out.push('people');
  if (d.equipment?.overdue) out.push('equipment');
  return out;
}
